import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Plus, Save, X, Check, Film } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, media, CATEGORIES } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Thumb = ({ p, className = "" }) => (
  <div className={`overflow-hidden bg-[#0b0b0b] ${className}`}>{p?.urls?.thumb && <img src={media(p.urls.thumb)} alt={p.title} className="h-full w-full object-cover" />}</div>
);

const Picker = ({ open, onClose, photos, multi, max = 12, initial = [], onPick, filterCat }) => {
  const [sel, setSel] = useState(initial);
  const [cat, setCat] = useState(filterCat || "");
  useEffect(() => { if (open) { setSel(initial); setCat(filterCat || ""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = photos.filter((p) => !cat || p.category === cat);
  const toggle = (id) => {
    if (!multi) { onPick([id]); onClose(); return; }
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= max ? s : [...s, id]));
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto border-white/10 bg-[#0f0f0f] text-white" data-testid="home-photo-picker">
        <DialogHeader><DialogTitle className="font-display">{multi ? `Choose photos (${sel.length}/${max})` : "Choose a photo"}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {["", ...CATEGORIES].map((c) => (
            <button key={c || "all"} onClick={() => setCat(c)} data-testid={`picker-filter-${c || "all"}`}
              className={`rounded-full border px-3 py-1.5 text-xs ${cat === c ? "border-white bg-white text-black" : "border-white/20 text-white/75"}`}>{c || "All"}</button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {list.map((p) => {
            const i = sel.indexOf(p.id);
            return (
              <button key={p.id} onClick={() => toggle(p.id)} data-testid={`picker-photo-${p.id}`} className={`relative aspect-square border-2 ${i >= 0 ? "border-[#5d9a35]" : "border-transparent hover:border-white/40"}`}>
                <Thumb p={p} className="h-full w-full" />
                {i >= 0 && <span className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center bg-[#5d9a35] font-mono text-[11px] text-white">{multi ? i + 1 : <Check size={12} />}</span>}
                <span className="absolute inset-x-0 bottom-0 truncate bg-black/70 px-1.5 py-1 text-left text-[10px] text-white">{p.title}</span>
              </button>
            );
          })}
        </div>
        {multi && (
          <div className="flex justify-end gap-2">
            <button onClick={() => setSel([])} className="border border-white/20 px-4 py-2 text-sm" data-testid="picker-clear">Clear</button>
            <button onClick={() => { onPick(sel); onClose(); }} className="bg-white px-5 py-2 text-sm font-semibold text-black" data-testid="picker-confirm">Use {sel.length} photo{sel.length === 1 ? "" : "s"}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Block = ({ n, title, hint, children, testid }) => (
  <section className="border border-white/10 bg-[#0f0f0f] p-5" data-testid={testid}>
    <div className="kicker mb-1">{n} — {title}</div>
    {hint && <p className="mb-4 text-xs text-white/55">{hint}</p>}
    {children}
  </section>
);

const OrderedList = ({ ids, byId, onChange, onAdd, testid, emptyText }) => {
  const move = (i, d) => { const a = [...ids]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  return (
    <div className="flex flex-wrap gap-3" data-testid={testid}>
      {!ids.length && <p className="w-full text-sm text-white/55">{emptyText}</p>}
      {ids.map((id, i) => (
        <div key={id} className="w-36">
          <div className="relative aspect-[4/3]"><Thumb p={byId[id]} className="h-full w-full" /><span className="absolute left-1 top-1 bg-black/70 px-1.5 font-mono text-[10px] text-white">{i + 1}</span></div>
          <div className="mt-1 truncate text-xs text-white/75">{byId[id]?.title || "(removed photo)"}</div>
          <div className="flex justify-between text-white/60">
            <span className="flex"><button onClick={() => move(i, -1)} className="p-1 hover:text-white" data-testid={`${testid}-left-${i}`}><ArrowLeft size={13} /></button><button onClick={() => move(i, 1)} className="p-1 hover:text-white" data-testid={`${testid}-right-${i}`}><ArrowRight size={13} /></button></span>
            <button onClick={() => onChange(ids.filter((x) => x !== id))} className="p-1 hover:text-red-400" data-testid={`${testid}-remove-${i}`}><X size={13} /></button>
          </div>
        </div>
      ))}
      <button onClick={onAdd} data-testid={`${testid}-add`} className="flex aspect-[4/3] w-36 flex-col items-center justify-center gap-1 border border-dashed border-white/25 text-xs text-white/75 hover:border-white"><Plus size={16} /> Choose</button>
    </div>
  );
};

const Single = ({ id, byId, onPick, onClear, testid, autoText }) => (
  <div className="flex items-center gap-4" data-testid={testid}>
    <button onClick={onPick} data-testid={`${testid}-choose`} className="relative aspect-[4/3] w-40 shrink-0 border border-dashed border-white/25 hover:border-white">
      {id && byId[id] ? <Thumb p={byId[id]} className="h-full w-full" /> : <span className="flex h-full items-center justify-center text-xs text-white/60"><Plus size={14} className="mr-1" /> Choose</span>}
    </button>
    <div className="min-w-0 text-sm">
      <div className="truncate text-white">{id && byId[id] ? byId[id].title : autoText}</div>
      {id && <button onClick={onClear} className="mt-1 text-xs text-white/55 underline hover:text-white" data-testid={`${testid}-clear`}>Use automatic</button>}
    </div>
  </div>
);

export const HomepageAdmin = ({ photos }) => {
  const { setSettings } = useLang();
  const [s, setS] = useState(null);
  const [videos, setVideos] = useState([]);
  const [pick, setPick] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/content/videos").then((r) => setVideos(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const ready = useMemo(() => photos.filter((p) => p.status === "ready"), [photos]);
  const byId = useMemo(() => Object.fromEntries(photos.map((p) => [p.id, p])), [photos]);
  if (!s) return null;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const covers = s.home_category_covers || {};
  const save = async () => {
    try { const r = await api.put("/admin/settings", s); setSettings(r.data); toast.success("Homepage saved — changes are live"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const open = (cfg) => setPick(cfg);
  return (
    <div className="max-w-5xl space-y-5" data-testid="homepage-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-white/65">Choose exactly which photographs and film appear on the homepage. Anything left empty is filled automatically.</p>
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="border border-white/20 px-4 py-2.5 text-sm text-white" data-testid="homepage-preview-link">Preview homepage</a>
          <button onClick={save} data-testid="homepage-save-button" className="flex items-center gap-2 bg-white px-5 py-2.5 text-sm font-semibold text-black"><Save size={14} /> Save homepage</button>
        </div>
      </div>
      <Block n="01" title="Hero slideshow" hint="Up to 5 full-screen photos, in this order. Landscape (horizontal) photos look best." testid="home-block-hero">
        <OrderedList testid="home-hero" ids={s.home_hero_ids || []} byId={byId} onChange={(v) => set("home_hero_ids", v)} emptyText="Automatic: photos marked as hero."
          onAdd={() => open({ multi: true, max: 5, initial: s.home_hero_ids || [], onPick: (v) => set("home_hero_ids", v) })} />
      </Block>
      <Block n="02" title="Explore the World — category cards" hint="The cover photo of each gallery card." testid="home-block-covers">
        <div className="grid gap-4 sm:grid-cols-2">
          {CATEGORIES.map((c) => (
            <div key={c}><div className="mb-2 text-xs uppercase tracking-widest text-white/55">{c}</div>
              <Single testid={`home-cover-${c}`} id={covers[c]} byId={byId} autoText="Automatic: first featured photo"
                onPick={() => open({ filterCat: c, onPick: ([id]) => set("home_category_covers", { ...covers, [c]: id }) })}
                onClear={() => { const n = { ...covers }; delete n[c]; set("home_category_covers", n); }} />
            </div>
          ))}
        </div>
      </Block>
      <Block n="03" title="Framed print section" hint="The photo shown framed in the living room." testid="home-block-prints">
        <Single testid="home-prints" id={s.home_prints_photo_id} byId={byId} autoText="Automatic: featured wildlife photo"
          onPick={() => open({ onPick: ([id]) => set("home_prints_photo_id", id) })} onClear={() => set("home_prints_photo_id", "")} />
      </Block>
      <Block n="04" title="Latest Photos grid" hint="Up to 12 photos, in this order. Empty = your newest photos automatically." testid="home-block-latest">
        <OrderedList testid="home-latest" ids={s.home_latest_ids || []} byId={byId} onChange={(v) => set("home_latest_ids", v)} emptyText="Automatic: newest photos."
          onAdd={() => open({ multi: true, max: 12, initial: s.home_latest_ids || [], onPick: (v) => set("home_latest_ids", v) })} />
      </Block>
      <Block n="05" title="Featured film" hint="Add or edit films in the Videos tab." testid="home-block-video">
        <div className="flex items-center gap-3">
          <Film size={18} className="text-white/60" />
          <select value={s.home_video_id || ""} onChange={(e) => set("home_video_id", e.target.value)} data-testid="home-video-select" className="border border-white/15 bg-[#0b0b0b] px-3 py-2.5 text-sm text-white">
            <option value="">Automatic: film marked “featured”</option>
            {videos.map((v) => <option key={v.id} value={v.id}>{v.title}{v.video_url ? "" : " (no link yet)"}</option>)}
          </select>
        </div>
      </Block>
      <Block n="06" title="Newsletter background" hint="The wide photo behind “Join the Adventure”." testid="home-block-newsletter">
        <Single testid="home-newsletter" id={s.home_newsletter_photo_id} byId={byId} autoText="Automatic: first travel photo"
          onPick={() => open({ onPick: ([id]) => set("home_newsletter_photo_id", id) })} onClear={() => set("home_newsletter_photo_id", "")} />
      </Block>
      <div className="flex justify-end"><button onClick={save} data-testid="homepage-save-button-bottom" className="flex items-center gap-2 bg-white px-5 py-2.5 text-sm font-semibold text-black"><Save size={14} /> Save homepage</button></div>
      <Picker open={!!pick} onClose={() => setPick(null)} photos={ready} multi={!!pick?.multi} max={pick?.max} initial={pick?.initial || []} filterCat={pick?.filterCat} onPick={(v) => pick?.onPick(v)} />
    </div>
  );
};
